import { c as _c } from "react/compiler-runtime";
import { useMutation } from '@apollo/client';
import { FileIcon, useDownloadFile } from '@newfront-insurance/admin-ui/files';
import { Badge, cn } from '@newfront-insurance/core-ui/v2';
import { graphql } from '@newfront-insurance/graphql-frontend';
import { useNotifications } from '@newfront-insurance/next-notifications';
import { Response, Sources, SourcesContent, SourcesTrigger } from '@newfront-insurance/nova-core';
import { useProvider } from '@newfront-insurance/react-provision';
import { AnimatePresence, motion } from 'framer-motion';
import { Check, ClipboardList } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useCopyToClipboard } from 'react-use';
import { MarkdownWithCitations } from './markdown-with-citations';
import type { MessageReaction, UIMessageWithMetadata } from '../../../lib/types';
import { sanitizeText } from '../../../lib/utils';
import { ThumbsDown } from '../../icons/thumbs-down';
import { ThumbsUp } from '../../icons/thumbs-up';
import { MessageAction, MessageActions } from '../../message-actions';
import { type DataPartRenderer, type ToolOutputRenderer } from '../lib/default-renderers';
import { extractSourcesFromMessage, getSourcesArray, hasSources, type SourcesMap } from '../lib/sources';
import { useNovaContext } from '../providers';
const ADD_MESSAGE_REACTION = graphql(`
  mutation AddMessageReaction($messageUuid: String!, $reaction: MessageReaction) {
    updateMessageReaction(messageUuid: $messageUuid, reaction: $reaction) {
      uuid
      reaction
    }
  }
`);
interface PreviewMessageProps {
  message: UIMessageWithMetadata;
  isStreaming: boolean;
  isLast: boolean;
  onReaction: (reaction: MessageReaction | null) => void;
  /**
   * Sources accumulated from messages before the current one.
   * Used to resolve follow-up citations when source-url parts are omitted.
   */
  sourceFallbackMap?: SourcesMap;
  /**
   * Object mapping tool names to their renderer functions.
   * If provided, these renderers will be used instead of default rendering.
   */
  toolRenderers?: Record<string, ToolOutputRenderer>;
  /**
   * Object mapping data part types to their renderer functions.
   * If provided, these renderers will be used instead of default rendering.
   */
  dataPartRenderers?: Record<string, DataPartRenderer>;
  /**
   * Optional renderer function for content to display inside the message bubble
   * (for user messages) before the text content. Receives the message as a parameter.
   */
  beforeContentRenderer?: (message: UIMessageWithMetadata) => ReactNode;
  /**
   * Optional className to apply to the wrapper around beforeContentRenderer output.
   * Useful for adjusting spacing or positioning of the before-content element.
   */
  beforeContentClassName?: string;
  /**
   * Optional className to apply to the message content container (the bubble).
   * Useful for adjusting padding or other styling of the message bubble.
   */
  messageContentClassName?: string;
  /**
   * Controls whether inline citations and sources panel are rendered.
   * When false, falls back to plain markdown rendering using Response component.
   * Defaults to true for backward compatibility.
   */
  enableSourcesRendering?: boolean;
}

/**
 * Renders text with the slash command handle highlighted in fire-400 color.
 * Extracts the handle from the beginning of the text (e.g., "/compare-policies").
 * Preserves whitespace formatting exactly as typed.
 */
function SlashCommandHighlightedText(t0) {
  const $ = _c(9);
  const {
    text
  } = t0;
  let t1;
  if ($[0] !== text) {
    t1 = text.match(/^(\/[\w-]+)(.*)/s);
    $[0] = text;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const match = t1;
  if (!match) {
    let t2;
    if ($[2] !== text) {
      t2 = <span className="whitespace-pre-wrap">{text}</span>;
      $[2] = text;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    return t2;
  }
  const [, handle, rest] = match;
  let t2;
  if ($[4] !== handle) {
    t2 = <span className="text-fire-400">{handle}</span>;
    $[4] = handle;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== rest || $[7] !== t2) {
    t3 = <span className="whitespace-pre-wrap">{t2}{rest}</span>;
    $[6] = rest;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
}

/**
 * Renders the message text content with appropriate formatting based on message type.
 */
function MessageTextContent(t0) {
  const $ = _c(9);
  const {
    message,
    sanitizedText,
    messageHasSources,
    sourcesMap,
    enableSourcesRendering
  } = t0;
  if (messageHasSources && message.role === "assistant" && enableSourcesRendering !== false) {
    let t1;
    if ($[0] !== message.id || $[1] !== sanitizedText || $[2] !== sourcesMap) {
      t1 = <MarkdownWithCitations messageUuid={message.id} sourcesMap={sourcesMap} className="size-full [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">{sanitizedText}</MarkdownWithCitations>;
      $[0] = message.id;
      $[1] = sanitizedText;
      $[2] = sourcesMap;
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    return t1;
  }
  if (message.role === "user" && message.metadata?.slashCommandUuid) {
    let t1;
    if ($[4] !== sanitizedText) {
      t1 = <SlashCommandHighlightedText text={sanitizedText} />;
      $[4] = sanitizedText;
      $[5] = t1;
    } else {
      t1 = $[5];
    }
    return t1;
  }
  let t1;
  if ($[6] !== message.id || $[7] !== sanitizedText) {
    t1 = <Response messageUuid={message.id}>{sanitizedText}</Response>;
    $[6] = message.id;
    $[7] = sanitizedText;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  return t1;
}
function ChatFileListItem(t0) {
  const $ = _c(17);
  const {
    file
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {};
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    download,
    isDownloading
  } = useDownloadFile(t1);
  let t2;
  if ($[1] !== download || $[2] !== file.uuid) {
    t2 = () => {
      download(file.uuid);
    };
    $[1] = download;
    $[2] = file.uuid;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const handleClick = t2;
  let t3;
  if ($[4] !== handleClick) {
    t3 = e => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        handleClick();
      }
    };
    $[4] = handleClick;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== isDownloading) {
    t4 = cn("flex w-fit max-w-xs cursor-pointer items-center gap-2 rounded-sm px-1 py-1", "hover:bg-muted/50 transition-colors", {
      "pointer-events-none opacity-50": isDownloading
    });
    $[6] = isDownloading;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  let t6;
  if ($[8] !== file.filename) {
    t5 = <FileIcon filename={file.filename} hasBackground={false} />;
    t6 = <span className="text-steel-400 truncate text-xs">{file.filename}</span>;
    $[8] = file.filename;
    $[9] = t5;
    $[10] = t6;
  } else {
    t5 = $[9];
    t6 = $[10];
  }
  let t7;
  if ($[11] !== handleClick || $[12] !== t3 || $[13] !== t4 || $[14] !== t5 || $[15] !== t6) {
    t7 = <div role="button" tabIndex={0} onClick={handleClick} onKeyDown={t3} className={t4}>{t5}{t6}</div>;
    $[11] = handleClick;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  return t7;
}
export function PreviewMessage({
  message,
  isStreaming,
  isLast,
  onReaction,
  sourceFallbackMap,
  toolRenderers,
  dataPartRenderers,
  beforeContentRenderer,
  beforeContentClassName,
  messageContentClassName,
  enableSourcesRendering
}: PreviewMessageProps) {
  const notifications = useNotifications();
  const [updateMessageReaction] = useMutation(ADD_MESSAGE_REACTION, {});
  const {
    authProvider
  } = useNovaContext();
  const {
    authState
  } = useProvider(authProvider);
  const isPublicMode = !authState.isLoggedIn;

  // Extract sources from message parts
  const messageHasSources = hasSources(message, sourceFallbackMap);
  const sourcesMap = useMemo(() => messageHasSources ? extractSourcesFromMessage(message, sourceFallbackMap) : new Map(), [message, messageHasSources, sourceFallbackMap]);
  const sourcesArray = useMemo(() => sourcesMap.size > 0 ? getSourcesArray(sourcesMap) : [], [sourcesMap]);
  const handleReaction = (newReaction: MessageReaction) => {
    // Toggle reaction - if same reaction clicked, remove it
    const reaction = message.metadata?.reaction === newReaction ? null : newReaction;
    if (!isPublicMode) {
      updateMessageReaction({
        variables: {
          messageUuid: message.id,
          reaction
        },
        optimisticResponse: {
          updateMessageReaction: {
            uuid: message.id,
            reaction
          }
        }
      });
    }
    onReaction(reaction);
    if (reaction) {
      notifications.add({
        type: 'success',
        duration: 2000,
        title: 'Thank you for your feedback!'
      });
    }
  };
  const [_, copyToClipboard] = useCopyToClipboard();
  const [isCopied, setIsCopied] = useState(false);

  // Reset copied state after 2 seconds
  useEffect(() => {
    if (isCopied) {
      const timer = setTimeout(() => {
        setIsCopied(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [isCopied]);
  function handleCopyToClipboard() {
    const lastMessagePart = message.parts?.[message.parts.length - 1];
    if (!lastMessagePart) {
      return;
    }
    copyToClipboard(lastMessagePart.type === 'text' ? lastMessagePart.text : '');
    setIsCopied(true);
  }
  return <AnimatePresence>
      <motion.div data-testid={`message-${message.role}`} className="group/message mx-auto w-full max-w-[800px] sm:px-8" initial={{
      y: 5,
      opacity: 0
    }} animate={{
      y: 0,
      opacity: 1
    }} data-role={message.role} key={message.id}>
        <div className={cn('flex w-full items-start gap-4 group-data-[role=user]/message:ml-auto group-data-[role=user]/message:max-w-full')}>
          <div className="flex w-full flex-col gap-2">
            {message.metadata?.files && message.metadata.files.length > 0 && <div className="flex flex-row flex-wrap gap-2">
                {message.metadata?.files?.map(file => <ChatFileListItem key={file.uuid} file={file} />)}
              </div>}
            <div className={cn({
            'ml-auto max-w-full': message.role === 'user'
          })}>
              {message.parts?.map((part, index) => {
              const {
                type
              } = part;
              const key = `message-${message.id}-part-${index}`;

              // Check for custom tool output renderer
              if (type.startsWith('tool-')) {
                const toolName = type.replace('tool-', '');
                if (toolRenderers?.[toolName]) {
                  const rendered = toolRenderers[toolName](part, message.id);
                  if (rendered) {
                    return <div key={key}>{rendered}</div>;
                  }
                }
                return null;
              }
              if (type.startsWith('data-')) {
                const dataPartType = type.replace('data-', '');
                const DataPartComponent = dataPartRenderers?.[dataPartType];
                if (DataPartComponent) {
                  return <DataPartComponent key={key} />;
                }
              }
              if (type === 'text') {
                const sanitizedText = sanitizeText(part.text);
                return <div key={key} className="flex flex-row items-start gap-2">
                      <div data-testid="message-content" className={cn('flex max-w-full flex-col gap-4 rounded-[20px] py-2', {
                    'bg-muted rounded-br-[0px]': message.role === 'user',
                    'rounded-bl-[0px]': message.role === 'assistant',
                    'px-5': message.role === 'user'
                  }, messageContentClassName)}>
                        {beforeContentRenderer && message.role === 'user' && <div className={beforeContentClassName}>{beforeContentRenderer(message)}</div>}
                        <MessageTextContent message={message} sanitizedText={sanitizedText} messageHasSources={messageHasSources} sourcesMap={sourcesMap} enableSourcesRendering={enableSourcesRendering} />
                      </div>
                    </div>;
              }
              return null;
            })}
            </div>

            {/* Show sources panel for assistant messages with sources */}
            {message.role === 'assistant' && sourcesArray.length > 0 && enableSourcesRendering !== false && <Sources>
                <SourcesTrigger count={sourcesArray.length} />
                <SourcesContent>
                  <div className="flex flex-wrap gap-2">
                    {sourcesArray.map((source, index_0) => <a key={source.sourceId} href={source.url} target="_blank" rel="noopener noreferrer" className="no-underline">
                        <Badge variant="secondary" className="hover:bg-secondary/80 cursor-pointer transition-colors">
                          {index_0 + 1}. {source.title}
                        </Badge>
                      </a>)}
                  </div>
                </SourcesContent>
              </Sources>}

            {/* Show reactions only for assistant messages */}
            {message.role === 'assistant' && !(isStreaming && isLast) && <div className="transition-opacity">
                <MessageActions>
                  <MessageAction tooltip={isCopied ? 'Copied!' : 'Copy to clipboard'} label={isCopied ? 'Copied!' : 'Copy to clipboard'} onClick={() => handleCopyToClipboard()}>
                    {isCopied ? <Check aria-label="Copied" color="currentColor" size={18} /> : <ClipboardList aria-label="Copy to clipboard" color="currentColor" size={18} />}
                  </MessageAction>
                  {(message.metadata?.reaction === 'THUMBS_UP' || !message.metadata?.reaction) && <MessageAction tooltip="Good response" label="Thumbs up" isActive={message.metadata?.reaction === 'THUMBS_UP'} onClick={() => handleReaction('THUMBS_UP')}>
                      <ThumbsUp aria-label="Thumbs up" active={message.metadata?.reaction === 'THUMBS_UP'} />
                    </MessageAction>}
                  {(message.metadata?.reaction === 'THUMBS_DOWN' || !message.metadata?.reaction) && <MessageAction tooltip="Bad response" label="Thumbs down" isActive={message.metadata?.reaction === 'THUMBS_DOWN'} aria-label="Thumbs down" onClick={() => handleReaction('THUMBS_DOWN')}>
                      <ThumbsDown aria-label="Thumbs down" active={message.metadata?.reaction === 'THUMBS_DOWN'} />
                    </MessageAction>}
                </MessageActions>
              </div>}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>;
}